import { App as AntApp, ConfigProvider, theme } from 'antd';
// ESM build: the CommonJS 'antd/locale/ru_RU' default-imports as { default } in this
// "type": "module" package, and antd silently falls back to English.
import ruRU from 'antd/es/locale/ru_RU';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { PRIMARY_COLOR, ThemeContext, type ThemeMode } from './themeContext';

const STORAGE_KEY = 'sos.theme';

function storedMode(): ThemeMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage unavailable: nothing was chosen that we can see.
  }
  return null;
}

function systemMode(): ThemeMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(() => storedMode() ?? systemMode());
  // Only the switch stores a choice: a screen's own default must not become the user's.
  const [chosen, setChosen] = useState(() => storedMode() !== null);

  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const toggle = useCallback(() => {
    setChosen(true);
    setMode((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Not persisted; the choice lasts for this page only.
      }
      return next;
    });
  }, []);

  const prefer = useCallback(
    (preferred: ThemeMode) => {
      if (!chosen) setMode(preferred);
    },
    [chosen],
  );

  const state = useMemo(() => ({ mode, toggle, prefer }), [mode, toggle, prefer]);

  return (
    <ThemeContext.Provider value={state}>
      <ConfigProvider
        locale={ruRU}
        theme={{
          algorithm: mode === 'dark' ? theme.darkAlgorithm : theme.defaultAlgorithm,
          token: {
            colorPrimary: PRIMARY_COLOR,
            colorInfo: PRIMARY_COLOR,
            // TZ 15.1: at least 14px on the web.
            fontSize: 14,
          },
        }}
      >
        <AntApp>{children}</AntApp>
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}
